// Phase 130 §4b — demo-handoff claim hook.
//
// SINGLE RESPONSIBILITY: capture the marketing-CTA `?handoff=<jwt>` query
// param into sessionStorage and (once the user is authed in their active
// workspace) POST /sandbox/claim-handoff so the orphan demo_anonymous
// SandboxSession adopts into the workspace.
//
// Why a hook (not inline in WorkspaceProvider):
//
//   - WorkspaceProvider stays focused on workspace state (workspaces list,
//     active id, channels, usage, threshold notifications).
//   - The capture-then-claim sequence is testable in isolation.
//   - Future flows (e.g. an authenticated /sandbox/share?handoff= path) can
//     reuse the same hook elsewhere in the tree without duplicating the
//     fetch/PostHog/error-handling boilerplate.
//
// Why one effect instead of two (capture + claim):
//
//   - Reading from `useSearchParams()` here means every URL with `?handoff=`
//     anywhere in the authed tree gets captured and claimed in one tick — no
//     race between an outer URL-capture component and an inner claim
//     component.
//
// Idempotency:
//
//   - `peekHandoffJwt()` (read-only) → POST → `consumeHandoffJwt()` (clear)
//     keeps the header-injection path in `authFetch` (ordering A) and the
//     explicit claim (ordering C) working without stepping on each other.
//   - `claimAttemptedRef` guards against effect re-fires on dependency
//     churn after the POST has been issued.
//
// Best-effort:
//
//   - Any error is swallowed. The /sandbox UI's manual bind path is the
//     fallback if the claim never succeeds.

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import {
  consumeHandoffJwt,
  peekHandoffJwt,
  setHandoffJwt,
} from '@/lib/handoff-storage';
import { emit } from '@/lib/analytics';
import type { Workspace } from '@/types/workspace';

interface UseDemoHandoffClaimArgs {
  apiUrl: string;
  activeWorkspace: Workspace | null;
  sessionScoped: boolean;
}

export function useDemoHandoffClaim({
  apiUrl,
  activeWorkspace,
  sessionScoped,
}: UseDemoHandoffClaimArgs): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const claimAttemptedRef = useRef(false);

  // 1. Capture: ?handoff=<jwt> → sessionStorage. Strip the param so refresh
  //    doesn't re-store. Runs every render where the param is present.
  useEffect(() => {
    const fromUrl = searchParams.get('handoff');
    if (!fromUrl || fromUrl.length === 0) return;
    setHandoffJwt(fromUrl);
    const next = new URLSearchParams(searchParams);
    next.delete('handoff');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // 2. Claim: once activeWorkspace + sessionScoped are true and storage has
  //    a JWT, POST /sandbox/claim-handoff and clear storage on success.
  useEffect(() => {
    if (claimAttemptedRef.current) return;
    if (!activeWorkspace || !sessionScoped) return;
    const stored = peekHandoffJwt();
    if (!stored) return;

    claimAttemptedRef.current = true;
    void (async () => {
      try {
        const res = await fetch(`${apiUrl}/sandbox/claim-handoff`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'X-Workspace-Id': activeWorkspace.id,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ handoffJwt: stored }),
        });
        if (!res.ok) return;

        const body = (await res.json().catch(() => null)) as
          | { sandboxSessionPublicId: string | null }
          | null;
        consumeHandoffJwt();

        if (body?.sandboxSessionPublicId) {
          emit('demo_claim_completed_authed', {
            workspace_id: activeWorkspace.id,
            sandbox_session_public_id: body.sandboxSessionPublicId,
          });
        }
      } catch {
        // Best-effort — the /sandbox UI's manual bind path remains as a
        // fallback if the claim never succeeds.
      }
    })();
  }, [apiUrl, activeWorkspace, sessionScoped, searchParams]);
}
